import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react';
import { Buildings, CaretDown, MagnifyingGlass } from 'phosphor-react';
import { useLocation } from 'react-router-dom';

import { useFinanceiroFranquias } from '../../../hooks/useFinanceiro';
import { EntitySearch } from './components/EntitySearch';
import { InfoPopover } from './components/InfoPopover';
import { PeriodPicker } from './components/PeriodPicker';
import { FIN } from './components/theme';
import { useFinanceiroFilters } from './components/useFinanceiroFilters';

/**
 * Topbar GLOBAL do módulo Financeiro — período + franquia + busca, fincada uma
 * única vez no layout (fora do {children}), persistente no hub e em TODAS as
 * sub-rotas. Espelha o padrão da Auditoria Inteligente. O estado vive na URL
 * (useFinanceiroFilters), então a barra e as telas ficam sincronizadas sem
 * prop-drilling: a barra escreve na querystring, cada tela lê de lá.
 */
export const FinanceiroTopbar = () => {
  const f = useFinanceiroFilters();
  // Contratos são vigentes — o período não se aplica; some o controle inerte.
  // (Auditoria e Inadimplência respondem a período e busca normalmente.)
  const { pathname } = useLocation();
  const seg = pathname.split('/modulesHome/financeiro/')[1]?.split('/')[0] ?? '';
  const hidePeriodo = seg === 'contratos';
  const { data: franquiasData } = useFinanceiroFranquias();
  const franquias = franquiasData?.franquias ?? [];
  const acc = FIN.info;

  // Busca dentro do popover de franquia.
  const [filtroFr, setFiltroFr] = useState('');
  const franquiasFiltradas = franquias.filter(fr =>
    fr.toLowerCase().includes(filtroFr.trim().toLowerCase()),
  );
  const toggleFranquia = (fr: string) =>
    f.setContaOrigem(
      f.contaOrigem.includes(fr) ? f.contaOrigem.filter(x => x !== fr) : [...f.contaOrigem, fr],
    );

  const nFr = f.contaOrigem.length;
  const [primeiraFr] = f.contaOrigem;
  let labelFranquia = 'Consolidado';
  if (nFr === 1) labelFranquia = primeiraFr;
  else if (nFr > 1) labelFranquia = `${nFr} franquias`;

  let escopo = `Todas as bases · ${franquias.length} ${
    franquias.length === 1 ? 'franquia' : 'franquias'
  }`;
  if (nFr === 1) escopo = 'Base única · não consolida';
  else if (nFr > 1) escopo = `${nFr} de ${franquias.length} franquias`;

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={30}
      bg="rgba(255,255,255,0.72)"
      backdropFilter="blur(16px)"
      borderBottom="1px solid rgba(15,23,42,0.06)"
      boxShadow="0 2px 10px rgba(20,40,60,0.05)"
      px={{ base: 3, md: 6 }}
      py={2.5}
    >
      <Flex maxW="1320px" mx="auto" gap={3} align="center" wrap="wrap">
        {/* Período — selects mês/ano + atalhos (PeriodPicker) */}
        {!hidePeriodo && (
          <PeriodPicker
            range={f.range}
            onMonths={f.setRangeMonths}
            atalhos={f.atalhos}
            accent={acc}
          />
        )}

        {/* Franquia (pill com busca) + metadados de escopo */}
        {franquias.length > 0 && (
          <Flex align="center" gap={2.5}>
            <Popover placement="bottom-start" closeOnBlur>
              <PopoverTrigger>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Buildings size={16} weight="duotone" />}
                  rightIcon={<CaretDown size={12} />}
                  fontWeight={600}
                  maxW="230px"
                  borderColor={`${acc}40`}
                >
                  <Box as="span" noOfLines={1}>
                    {labelFranquia}
                  </Box>
                </Button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="300px" boxShadow="xl" zIndex={1500}>
                  <PopoverArrow />
                  <PopoverBody p={2}>
                    <Flex
                      align="center"
                      gap={2}
                      px={2}
                      py={1.5}
                      mb={2}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="rgba(15,23,42,0.1)"
                    >
                      <MagnifyingGlass size={14} color="#94A3B8" />
                      <Input
                        variant="unstyled"
                        fontSize="sm"
                        placeholder="Buscar franquia…"
                        value={filtroFr}
                        onChange={e => setFiltroFr(e.target.value)}
                      />
                    </Flex>
                    <Box
                      as="button"
                      w="full"
                      textAlign="left"
                      px={2}
                      py={1.5}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight={f.contaOrigem.length === 0 ? 700 : 500}
                      color={f.contaOrigem.length === 0 ? acc : '#0F172A'}
                      _hover={{ bg: 'rgba(15,23,42,0.04)' }}
                      onClick={() => f.setContaOrigem([])}
                    >
                      Consolidado (todas)
                    </Box>
                    <Divider my={2} />
                    <Box maxH="240px" overflowY="auto">
                      {franquiasFiltradas.map(fr => {
                        const on = f.contaOrigem.includes(fr);
                        return (
                          <Flex
                            key={fr}
                            as="button"
                            w="full"
                            align="center"
                            gap={2}
                            px={2}
                            py={1.5}
                            borderRadius="md"
                            _hover={{ bg: 'rgba(15,23,42,0.04)' }}
                            onClick={() => toggleFranquia(fr)}
                          >
                            <Box
                              w="15px"
                              h="15px"
                              borderRadius="sm"
                              borderWidth="1.5px"
                              borderColor={on ? acc : '#CBD5E1'}
                              bg={on ? acc : 'transparent'}
                              flexShrink={0}
                            />
                            <Text fontSize="sm" noOfLines={1} textAlign="left">
                              {fr}
                            </Text>
                          </Flex>
                        );
                      })}
                      {franquiasFiltradas.length === 0 && (
                        <Text fontSize="xs" color="#94A3B8" px={2} py={3}>
                          Nenhuma franquia.
                        </Text>
                      )}
                    </Box>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
            <Text
              fontSize="xs"
              color="#64748B"
              fontWeight={500}
              noOfLines={1}
              display={{ base: 'none', lg: 'block' }}
            >
              {escopo}
            </Text>
          </Flex>
        )}

        {/* Busca de entidade (cliente/fornecedor) — autocomplete rico */}
        <EntitySearch
          value={f.q}
          onChange={f.setQ}
          contaOrigem={f.contaOrigem.length ? f.contaOrigem.join(',') : undefined}
          accent={acc}
          placeholder="Buscar por ID, CNPJ ou Razão Social…"
        />

        <Box ml="auto">
          <InfoPopover title="Filtros do módulo" size={16}>
            <Text>
              <b>Período</b>: escolha De/Até por mês-ano ou use um atalho (este ano, trimestre, últ.
              12m). As telas de série temporal (fluxo, gaps) abrem no ano corrente.
            </Text>
            <Text mt={2}>
              <b>Busca</b>: por razão social, CNPJ/CPF ou ID. Ao escolher, o módulo passa a
              “carregar” a entidade e todas as telas ficam no recorte dela.
            </Text>
            <Text mt={2}>
              <b>Franquia</b>: busque e escolha uma ou mais bases do Conta Azul. “Consolidado” soma
              todas — cada base é contada uma única vez (sem duplicar).
            </Text>
            <Text mt={2}>
              Os filtros valem para todo o módulo e ficam na URL (dá para compartilhar o link).
            </Text>
          </InfoPopover>
        </Box>
      </Flex>
    </Box>
  );
};
